/**
 * Balloons float up from below the window, swaying on their strings, and
 * drift off the top. They're the one emote that takes clicks: pressing a
 * balloon pops it. Each has an invisible target over it that soaks up the
 * press, so popping never reaches the page underneath. The balloons are drawn
 * on the emote canvas rather than as elements, so a string passes behind the
 * balloon in front of it and keeps a steady width as it ripples.
 */
import { COLORS, calm, pick, rand, scene, type Frame } from "./scene";

const TAU = Math.PI * 2;

interface Mood {
  count: number;
  /** Launches are spread over this long, so they come up as a stream rather than a wall. */
  staggerMs: number;
  /** Seconds to cross the window, far (small) to near (big): nearer ones rise faster. */
  rise: [number, number];
  /** Sideways sway in pixels either way, and seconds a cycle, so neighbours drift in and out of step. */
  sway: [number, number];
  swayPeriod: [number, number];
  /** The ripple running down each string: pixels either side, and radians a second. */
  ripple: number;
  rippleSpeed: [number, number];
  /** The pop: how long the balloon takes to go, how much it swells as it does, whether spikes burst out, and how fast the loose string falls (px/s²). */
  popMs: number;
  swell: number;
  spikes: boolean;
  stringFall: number;
  /** Bits of rubber per pop, and their speed in pixels a 60 Hz frame. */
  bits: number;
  bitSpeed: [number, number];
}

const LIVELY: Mood = {
  count: 20,
  staggerMs: 3000,
  rise: [6.4, 5.2],
  sway: [14, 32],
  swayPeriod: [2.8, 4.2],
  ripple: 5,
  rippleSpeed: [4, 6],
  popMs: 90,
  swell: 0.22,
  spikes: true,
  stringFall: 1400,
  bits: 14,
  bitSpeed: [2, 5.5],
};

/** Fewer, slower, barely swaying, and a pop is a soft fade with a few slow bits. */
const GENTLE: Mood = {
  count: 6,
  staggerMs: 2500,
  rise: [8.6, 7.6],
  sway: [5, 10],
  swayPeriod: [4.5, 6],
  ripple: 2.5,
  rippleSpeed: [2, 3],
  popMs: 220,
  swell: 0,
  spikes: false,
  stringFall: 300,
  bits: 6,
  bitSpeed: [0.5, 1.5],
};

/** A narrow window gets one balloon per this many pixels across (but at least MIN_COUNT), so they don't pile up. */
const SPACING = 60;
const MIN_COUNT = 8;
/** Width in pixels, far to near, on a laptop-sized window. */
const WIDTH = [54, 80];
/** String length, in balloon widths. */
const STRING_LEN = [1.6, 2.1];
/** Slow sideways drift, pixels a second either way. */
const DRIFT = 8;
/** Lean into the sway: radians per pixel-a-second of sideways speed, up to MAX_LEAN. */
const LEAN = 0.0016;
const MAX_LEAN = 0.14;
/** The string trails the sway by this much of its cycle (radians), its loose end bowing back this many seconds of sideways speed. */
const STRING_LAG = 0.9;
const STRING_BOW = 0.35;
const STRING_WIDTH = 1.2;
const STRING_ALPHA = 0.8;
const STRING_SEGMENTS = 14;
/** Ripple cycles along a string. */
const RIPPLE_WAVES = 1.25;
/** Whatever happens (say the window is made much taller mid-flight), it's all gone by now, fading over the last FADE_MS. */
const CAP_MS = 14_000;
const FADE_MS = 600;

/** A balloon in a box 100 units wide: an egg, fuller at the top, pinched at the bottom into a knot. */
const BODY = "M50 2C78 2 97 22 97 50C97 80 70 106 50 116C30 106 3 80 3 50C3 22 22 2 50 2Z";
const KNOT = "M50 112L44.5 124.5Q50 127 55.5 124.5Z";
const BOX_H = 128;
/** The middle of the body, which the balloon leans about. */
const MID_Y = 58;
/** Where the string's tied (just inside the bottom of the knot), from the middle of the body, in widths. */
const TIE = (124 - MID_Y) / 100;
/** Sprite pixels per unit: enough to stay sharp on a 2x screen. */
const SPRITE_RES = 2;

/**
 * The target over each balloon, in widths: a little bigger than the balloon
 * and a little low, since it's rising while the click is on its way.
 */
const HIT_W = 1.02;
const HIT_H = 1.22;
const HIT_DROP = 0.05;
const HIT_STYLE =
  "position:absolute;left:0;top:0;border-radius:50% / 45% 45% 55% 55%;pointer-events:auto;cursor:pointer;touch-action:manipulation;" +
  "-webkit-tap-highlight-color:transparent;user-select:none;-webkit-user-select:none;will-change:transform;display:none";
/**
 * A popped balloon's target stays put until the click that popped it is
 * over (or this long, if the pointer slid off), so the rest of that click
 * can't land on the editor underneath.
 */
const HIT_LINGER_MS = 1000;
/** Pixels in a wheel "line", for mice that scroll by lines. */
const WHEEL_LINE = 16;
/** Other events on a balloon that shouldn't reach the page. Mouse and pointer ups go through, so a drag that ends over one still ends. */
const SWALLOWED = ["dblclick", "auxclick", "contextmenu", "touchstart"];

/** The pop's spikes, and how long the whole pop (body, spikes, falling string) takes. */
const SPIKES = 10;
const SPIKE_MS = 220;
const POP_MS = 700;
/** Bits move like confetti: pixels a 60 Hz frame, slowed by drag and pulled down. Life is in frames. */
const BIT_GRAVITY = 0.18;
const BIT_DRAG = 0.95;
const BIT_SIZE = [3, 7];
const BIT_LIFE = [32, 52];
const BIT_FADE = 12;

interface Balloon {
  color: string;
  sprite: HTMLCanvasElement;
  /** Width and string length in pixels. */
  w: number;
  len: number;
  /** Where across it launches, when, and how fast it rises (pixels a millisecond) from startY until its string clears the top at endY. */
  x: number;
  start: number;
  speed: number;
  startY: number;
  endY: number;
  sway: number;
  swayRate: number;
  swayPhase: number;
  drift: number;
  rippleRate: number;
  ripplePhase: number;
  /** Which way its pop's spikes point. */
  turn: number;
  state: "waiting" | "flying" | "popped" | "gone";
  /** The middle of its body, its lean, sideways speed (pixels a second) and string bow, as of the last frame; frozen when it pops. */
  cx: number;
  cy: number;
  lean: number;
  vx: number;
  bow: number;
  popAt: number;
  hit: HTMLDivElement | null;
}

interface Bit {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  angle: number;
  spin: number;
  /** Phase of the tumble, which squashes it as it flips over. */
  flip: number;
  color: string;
  /** Frames left; 0 or less is a free slot. */
  life: number;
}

function sprite(color: string): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 100 * SPRITE_RES;
  canvas.height = BOX_H * SPRITE_RES;
  const g = canvas.getContext("2d")!;
  g.scale(SPRITE_RES, SPRITE_RES);
  const body = new Path2D(BODY);
  const knot = new Path2D(KNOT);
  g.fillStyle = color;
  g.fill(knot);
  g.fill(body);
  // Lit from the top left: a sheen there and the far side in shade, so it reads as round.
  const shade = g.createRadialGradient(36, 34, 4, 50, MID_Y, 64);
  shade.addColorStop(0, "rgba(255,255,255,0.45)");
  shade.addColorStop(0.35, "rgba(255,255,255,0.06)");
  shade.addColorStop(0.7, "rgba(0,0,0,0)");
  shade.addColorStop(1, "rgba(0,0,0,0.28)");
  g.fillStyle = shade;
  g.fill(body);
  g.fillStyle = "rgba(0,0,0,0.2)";
  g.fill(knot);
  // The soft highlight, an oval along the curve of the body, and a glint in it.
  g.save();
  g.translate(31, 31);
  g.rotate(-0.5);
  g.scale(1, 1.9);
  const shine = g.createRadialGradient(0, 0, 0, 0, 0, 9);
  shine.addColorStop(0, "rgba(255,255,255,0.7)");
  shine.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = shine;
  g.beginPath();
  g.arc(0, 0, 9, 0, TAU);
  g.fill();
  g.fillStyle = "rgba(255,255,255,0.55)";
  g.beginPath();
  g.ellipse(-1.5, -3, 2, 2.4, 0, 0, TAU);
  g.fill();
  g.restore();
  // A faint rim, so the pale ones keep an edge against a near-white page.
  g.strokeStyle = "rgba(0,0,0,0.14)";
  g.lineWidth = 0.8;
  g.stroke(body);
  return canvas;
}

/** A string tied at (x, y), hanging along the balloon's lean, its loose end bowed `bow` pixels sideways, with a ripple running down it. */
function drawString(ctx: CanvasRenderingContext2D, b: Balloon, x: number, y: number, bow: number, ripple: number, phase: number) {
  const dx = -Math.sin(b.lean);
  const dy = Math.cos(b.lean);
  ctx.beginPath();
  ctx.moveTo(x, y);
  for (let i = 1; i <= STRING_SEGMENTS; i++) {
    const s = i / STRING_SEGMENTS;
    // Tied still at the top, freer towards the end.
    const off = ripple * s * (2 - s) * Math.sin(s * RIPPLE_WAVES * TAU - phase) + bow * s * s;
    ctx.lineTo(x + dx * s * b.len + dy * off, y + dy * s * b.len - dx * off);
  }
  ctx.stroke();
}

function drawBody(ctx: CanvasRenderingContext2D, b: Balloon, swell: number, alpha: number) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(b.cx, b.cy);
  ctx.rotate(b.lean);
  if (swell !== 1) ctx.scale(swell, swell);
  ctx.drawImage(b.sprite, -b.w / 2, (-MID_Y / 100) * b.w, b.w, (BOX_H / 100) * b.w);
  ctx.restore();
}

/** The pop's spikes, shooting out from the middle and shrinking away as they go. */
function drawSpikes(ctx: CanvasRenderingContext2D, b: Balloon, p: number, fade: number) {
  const outer = 0.55 + 0.55 * (1 - (1 - p) * (1 - p));
  const inner = 0.55 + 0.55 * p * p;
  ctx.globalAlpha = fade * (1 - p * 0.6);
  ctx.strokeStyle = b.color;
  ctx.lineWidth = 2.5 * (1 - p * 0.5);
  ctx.beginPath();
  for (let i = 0; i < SPIKES; i++) {
    const a = b.turn + (i / SPIKES) * TAU;
    const r = b.w * (i % 2 ? 0.6 : 0.8);
    const c = Math.cos(a);
    const s = Math.sin(a);
    ctx.moveTo(b.cx + c * r * inner, b.cy + s * r * inner);
    ctx.lineTo(b.cx + c * r * outer, b.cy + s * r * outer);
  }
  ctx.stroke();
}

/**
 * What the wheel would have scrolled at (x, y) if no balloon were there: the
 * nearest thing under the balloons that can scroll that way. Not the page
 * itself, which the wheel still reaches through the balloon.
 */
function scrollerAt(x: number, y: number, dx: number, dy: number, layer: HTMLElement): Element | null {
  const under = document.elementsFromPoint(x, y).find((el) => !layer.contains(el));
  for (let el = under ?? null; el && el !== document.scrollingElement && el !== document.body; el = el.parentElement) {
    const canY = dy !== 0 && el.scrollHeight > el.clientHeight;
    const canX = dx !== 0 && el.scrollWidth > el.clientWidth;
    if (!canX && !canY) continue;
    const style = getComputedStyle(el);
    if ((canY && /auto|scroll/.test(style.overflowY)) || (canX && /auto|scroll/.test(style.overflowX))) return el;
  }
  return null;
}

function release(b: Balloon) {
  b.hit?.remove();
  b.hit = null;
}

export default function play(): Promise<void> {
  const mood = calm() ? GENTLE : LIVELY;
  const W = innerWidth;
  const H = innerHeight;
  const n = Math.min(mood.count, Math.max(MIN_COUNT, Math.round(W / SPACING)));
  const scale = Math.min(1.15, Math.max(0.7, Math.min(W, H) / 850));
  const margin = Math.min(W * 0.12, 90 * scale);
  const ink = getComputedStyle(document.documentElement).getPropertyValue("--ink-3").trim() || "#7a7d76";

  // Spread across the window in slots, launched in a shuffled order, no two neighbours the same colour.
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const sprites = new Map<string, HTMLCanvasElement>();
  const balloons: Balloon[] = [];
  let prev = "";
  for (let i = 0; i < n; i++) {
    let color = pick(COLORS);
    while (color === prev) color = pick(COLORS);
    prev = color;
    if (!sprites.has(color)) sprites.set(color, sprite(color));
    const near = Math.random();
    const w = (WIDTH[0] + (WIDTH[1] - WIDTH[0]) * near) * scale;
    const len = w * rand(STRING_LEN[0], STRING_LEN[1]);
    const startY = H + (MID_Y / 100) * w + 8;
    const endY = -TIE * w - len - 8;
    const rise = mood.rise[0] + (mood.rise[1] - mood.rise[0]) * near + rand(-0.25, 0.25);
    balloons.push({
      color,
      sprite: sprites.get(color)!,
      w,
      len,
      x: margin + ((i + rand(0.2, 0.8)) / n) * (W - 2 * margin),
      start: ((order[i] + rand(0, 0.8)) / n) * mood.staggerMs,
      speed: (startY - endY) / (rise * 1000),
      startY,
      endY,
      sway: rand(mood.sway[0], mood.sway[1]) * scale,
      swayRate: TAU / rand(mood.swayPeriod[0], mood.swayPeriod[1]),
      swayPhase: rand(0, TAU),
      drift: rand(-DRIFT, DRIFT),
      rippleRate: rand(mood.rippleSpeed[0], mood.rippleSpeed[1]),
      ripplePhase: rand(0, TAU),
      turn: rand(0, TAU),
      state: "waiting",
      cx: 0,
      cy: startY,
      lean: 0,
      vx: 0,
      bow: 0,
      popAt: 0,
      hit: null,
    });
  }
  // Small (far) ones first, so near ones are drawn over them and take the click where they overlap.
  balloons.sort((a, b) => a.w - b.w);

  const layer = document.createElement("div");
  layer.setAttribute("aria-hidden", "true");
  layer.style.cssText = "position:fixed;inset:0;z-index:1000;pointer-events:none;overflow:hidden";
  balloons.forEach((b, i) => {
    const hit = document.createElement("div");
    hit.dataset.i = String(i);
    hit.style.cssText = `${HIT_STYLE};width:${b.w * HIT_W}px;height:${b.w * HIT_H}px`;
    layer.appendChild(hit);
    b.hit = hit;
  });
  document.body.appendChild(layer);

  // One pool, enough for every balloon to pop, so a pop never allocates.
  const bits: Bit[] = Array.from({ length: n * mood.bits }, () => ({ x: 0, y: 0, vx: 0, vy: 0, size: 0, angle: 0, spin: 0, flip: 0, color: "", life: 0 }));
  /** Scene time as of the last frame, for pops that land between frames. */
  let now = 0;

  const pop = (b: Balloon) => {
    b.state = "popped";
    b.popAt = now;
    let made = 0;
    for (const p of bits) {
      if (made === mood.bits) break;
      if (p.life > 0) continue;
      const a = rand(0, TAU);
      const speed = rand(mood.bitSpeed[0], mood.bitSpeed[1]);
      p.x = b.cx + Math.cos(a) * b.w * 0.4;
      p.y = b.cy + Math.sin(a) * b.w * 0.5;
      // Off in every direction, carrying some of the balloon's own drift and rise.
      p.vx = Math.cos(a) * speed + b.vx / 60;
      p.vy = Math.sin(a) * speed - b.speed * 16.67 * 0.5;
      p.size = rand(BIT_SIZE[0], BIT_SIZE[1]) * (b.w / 70);
      p.angle = rand(0, TAU);
      p.spin = rand(-0.3, 0.3);
      p.flip = rand(0, TAU);
      p.color = b.color;
      p.life = rand(BIT_LIFE[0], BIT_LIFE[1]);
      made++;
    }
  };

  const balloonFor = (e: Event): Balloon | undefined => balloons[Number((e.target as HTMLElement).dataset.i)];
  const onPointerDown = (e: PointerEvent) => {
    e.stopPropagation();
    const b = balloonFor(e);
    if (e.button === 0 && b?.state === "flying") pop(b);
  };
  // Cancelling the mousedown (not the pointerdown, which would skip it) is what keeps focus and the cursor where they are.
  const onMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onClick = (e: MouseEvent) => {
    e.stopPropagation();
    const b = balloonFor(e);
    if (b?.state === "popped") release(b);
  };
  // A balloon passing under the pointer mustn't stall scrolling, so the wheel is passed on to whatever's beneath it.
  const onWheel = (e: WheelEvent) => {
    if (e.ctrlKey) return;
    const unit = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? WHEEL_LINE : e.deltaMode === WheelEvent.DOM_DELTA_PAGE ? innerHeight : 1;
    const dx = (e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX) * unit;
    const dy = (e.shiftKey && !e.deltaX ? 0 : e.deltaY) * unit;
    scrollerAt(e.clientX, e.clientY, dx, dy, layer)?.scrollBy({ left: dx, top: dy, behavior: "instant" });
  };
  const swallow = (e: Event) => e.stopPropagation();
  layer.addEventListener("pointerdown", onPointerDown);
  layer.addEventListener("mousedown", onMouseDown);
  layer.addEventListener("click", onClick);
  layer.addEventListener("wheel", onWheel, { passive: true });
  for (const type of SWALLOWED) layer.addEventListener(type, swallow, { passive: true });

  const draw = ({ ctx, t, dt }: Frame) => {
    now = t;
    const fade = Math.max(0, Math.min(1, (CAP_MS - t) / FADE_MS));
    ctx.imageSmoothingQuality = "high";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    let left = 0;

    for (const b of balloons) {
      if (b.state === "gone") continue;
      if (b.state === "waiting") {
        if (t < b.start) {
          left++;
          continue;
        }
        b.state = "flying";
        if (b.hit) b.hit.style.display = "";
      }
      const phase = ((t - b.start) / 1000) * b.rippleRate + b.ripplePhase;

      if (b.state === "flying") {
        const s = (t - b.start) / 1000;
        const a = b.swayRate * s + b.swayPhase;
        b.cy = b.startY - b.speed * (t - b.start);
        if (b.cy < b.endY) {
          b.state = "gone";
          release(b);
          continue;
        }
        b.cx = b.x + b.sway * Math.sin(a) + b.drift * s;
        b.vx = b.sway * b.swayRate * Math.cos(a) + b.drift;
        b.lean = Math.max(-MAX_LEAN, Math.min(MAX_LEAN, b.vx * LEAN));
        b.bow = -(b.sway * b.swayRate * Math.cos(a - STRING_LAG) + b.drift) * STRING_BOW;
        if (b.hit) {
          const hw = b.w * HIT_W;
          const hh = b.w * HIT_H;
          b.hit.style.transform = `translate3d(${b.cx - hw / 2}px, ${b.cy + b.w * HIT_DROP - hh / 2}px, 0) rotate(${b.lean}rad)`;
        }
        ctx.globalAlpha = fade * STRING_ALPHA;
        ctx.strokeStyle = ink;
        ctx.lineWidth = STRING_WIDTH;
        drawString(ctx, b, b.cx - Math.sin(b.lean) * TIE * b.w, b.cy + Math.cos(b.lean) * TIE * b.w, b.bow, mood.ripple, phase);
        drawBody(ctx, b, 1, fade);
        left++;
        continue;
      }

      // Popped: the balloon swells and vanishes, spikes burst out, and the string falls away and fades.
      const age = t - b.popAt;
      if (age > HIT_LINGER_MS) release(b);
      if (age > POP_MS) {
        if (!b.hit) b.state = "gone";
        else left++;
        continue;
      }
      const k = age / POP_MS;
      const fall = 0.5 * mood.stringFall * (age / 1000) ** 2;
      ctx.globalAlpha = fade * STRING_ALPHA * (1 - k);
      ctx.strokeStyle = ink;
      ctx.lineWidth = STRING_WIDTH;
      drawString(ctx, b, b.cx - Math.sin(b.lean) * TIE * b.w, b.cy + Math.cos(b.lean) * TIE * b.w + fall, b.bow * (1 - k), mood.ripple, phase);
      if (age < mood.popMs) {
        const p = age / mood.popMs;
        drawBody(ctx, b, 1 + mood.swell * (1 - (1 - p) * (1 - p)), fade * (1 - p * p));
      }
      if (mood.spikes && age < SPIKE_MS) drawSpikes(ctx, b, age / SPIKE_MS, fade);
      left++;
    }

    const drag = BIT_DRAG ** dt;
    for (const p of bits) {
      if (p.life <= 0) continue;
      p.life -= dt;
      if (p.life <= 0) continue;
      left++;
      p.vx *= drag;
      p.vy = p.vy * drag + BIT_GRAVITY * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.angle += p.spin * dt;
      p.flip += 0.25 * dt;
      ctx.save();
      ctx.globalAlpha = fade * Math.min(1, p.life / BIT_FADE);
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.scale(1, Math.cos(p.flip));
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size * 0.35, p.size, p.size * 0.7);
      ctx.restore();
    }
    ctx.globalAlpha = 1;

    return left > 0 && t < CAP_MS;
  };

  return scene(draw).finally(() => {
    layer.removeEventListener("pointerdown", onPointerDown);
    layer.removeEventListener("mousedown", onMouseDown);
    layer.removeEventListener("click", onClick);
    layer.removeEventListener("wheel", onWheel);
    for (const type of SWALLOWED) layer.removeEventListener(type, swallow);
    layer.remove();
  });
}
